// ?use_ios_ui — twin of iOS 0.7.0 TopControlsView + WheelInfoPill: `+`
// top-left (hidden on the Place screen and at two wheels), the wheel pill
// centred (brand + model; tappable, with a chevron, only when a logo
// exists), `×` top-right, and a blue "Done →" when there is a page to
// return to.

import { ArrowRight, Camera, ChevronDown, Plus, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { IOS_UI_FONT, IOS_UI_PALETTE } from '@/components/ios-ui/ios-ui-palette/ios-ui-palette'
import { Spinner } from '@/components/spinner/spinner'

export interface TIosWheelInfo {
  brandName: string
  modelName: string
  brandLogoURL: string | null
}

interface IosTopBarProps {
  showAdd: boolean
  showDone: boolean
  wheelInfo: TIosWheelInfo | null
  onAdd: () => void
  onClose: () => void
  onDone: () => void
  onPillTap: () => void
  /** Close tapped and the session is tearing down. */
  closing?: boolean
  /** Takes an AR photo (2026-10-20 — Android's capture works, so the button
   *  iOS dropped returns, upper left). Absent: no button. */
  onCamera?: () => void
  /** Landscape has no status bar on this edge — the shipped TR container's
   *  10px inset rather than 33px. */
  isLandscape?: boolean
}

/** Status-bar clearance. Chrome's dom-overlay reports env(safe-area-inset-top)
 *  as 0, so the literal does the work (as on the shipped TL/TR containers). */
const TOP_CLEARANCE_PX = 33
const TOP_CLEARANCE_LANDSCAPE_PX = 10

export function IosTopBar({
  showAdd,
  showDone,
  wheelInfo,
  onAdd,
  onClose,
  onDone,
  onPillTap,
  closing = false,
  isLandscape = false,
  onCamera,
}: IosTopBarProps) {
  const showPill = wheelInfo != null && wheelInfo.brandName.length > 0 && wheelInfo.modelName.length > 0
  return (
    <div
      data-testid="ios-top-bar"
      style={{
        position: 'fixed',
        top: `calc(env(safe-area-inset-top, 0px) + ${isLandscape ? TOP_CLEARANCE_LANDSCAPE_PX : TOP_CLEARANCE_PX}px)`,
        left: 20,
        right: 20,
        height: 44,
        display: 'flex',
        alignItems: 'center',
        fontFamily: IOS_UI_FONT,
        pointerEvents: 'none',
        zIndex: 20,
      }}
    >
      {showPill && (
        <div style={{ position: 'absolute', left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
          <WheelInfoPill info={wheelInfo} onTap={onPillTap} />
        </div>
      )}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
      {onCamera != null && (
        <CircleButton testId="ios-camera" ariaLabel="Take photo" onClick={onCamera}>
          <Camera size={18} strokeWidth={2.25} />
        </CircleButton>
      )}
      {showAdd && (
        <CircleButton testId="ios-add-wheel" ariaLabel="Add Wheel" onClick={onAdd}>
          <Plus size={18} strokeWidth={2.5} />
        </CircleButton>
      )}
      </div>
      <div style={{ flex: 1 }} />
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <CircleButton testId="ios-close" ariaLabel="Close" onClick={onClose}>
          {closing ? <Spinner size={18} color="#000" trackColor="rgba(0,0,0,0.15)" /> : <X size={18} strokeWidth={2.5} />}
        </CircleButton>
        {showDone && (
          <button
            type="button"
            data-testid="ios-done"
            onClick={onDone}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '10px 16px',
              border: 'none',
              borderRadius: 22,
              background: IOS_UI_PALETTE.accent,
              color: IOS_UI_PALETTE.onAccent,
              fontSize: 16,
              fontWeight: 600,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              pointerEvents: 'auto',
              cursor: 'pointer',
            }}
          >
            Done
            <ArrowRight size={14} strokeWidth={2.5} />
          </button>
        )}
      </div>
    </div>
  )
}

function CircleButton({ testId, ariaLabel, onClick, children }: { testId: string; ariaLabel: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={ariaLabel}
      onClick={onClick}
      style={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        border: 'none',
        background: 'rgba(255, 255, 255, 0.9)',
        color: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        pointerEvents: 'auto',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  )
}

function WheelInfoPill({ info, onTap }: { info: TIosWheelInfo; onTap: () => void }) {
  const clickable = info.brandLogoURL != null
  return (
    <button
      type="button"
      data-testid="ios-wheel-pill"
      onClick={clickable ? onTap : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        padding: '5px 10px',
        border: 'none',
        borderRadius: 999,
        background: 'rgb(245, 245, 245)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
        pointerEvents: 'auto',
        cursor: clickable ? 'pointer' : 'default',
      }}
    >
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <span data-testid="ios-pill-brand" style={{ fontSize: 8, fontWeight: 500, color: 'rgb(102,102,102)', textTransform: 'uppercase' }}>
          {info.brandName}
        </span>
        <span data-testid="ios-pill-model" style={{ fontSize: 10, fontWeight: 600, color: 'rgb(38,38,38)' }}>
          {info.modelName}
        </span>
      </span>
      {clickable && (
        <span data-testid="ios-pill-chevron" style={{ display: 'inline-flex', color: 'rgb(89,89,89)' }}>
          <ChevronDown size={8} strokeWidth={3} />
        </span>
      )}
    </button>
  )
}
